// Selection logic for the interrupted-workout recovery banner.
//
// The banner surfaces an in-flight telemetry entry (left in IndexedDB when the
// tab died mid-workout) so the user can recover it. We only want to surface an
// entry once enough IDLE time has passed since the recorder last ran — a brief
// app switch shouldn't nag someone who is still training. Idle time is measured
// from `updatedAt` (the wall-clock epoch ms stamped on each 5 s flush), NOT from
// `startedAt`, because a long in-progress workout always started long ago.
//
// Everything here is pure except the two thin localStorage helpers at the
// bottom (load/record dismissals) — `selectRecoverableEntry` takes its inputs
// explicitly so it is trivially unit-testable.

import localStorageService from "../../services/localStorageService";

export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const DEFAULT_IDLE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

const DISMISSED_KEY = "khronos_telemetry_recovery_dismissed";

// A backfilled / device-less row is just `{ t }`. An entry is only worth
// recovering if at least one sample carries a real metric — otherwise the
// recording captured nothing and surfacing it is pure noise.
const METRIC_KEYS = ["w", "c", "sp", "spm", "p", "d", "hr"];
export const hasMeaningfulData = (entry) =>
  Array.isArray(entry?.samples) &&
  entry.samples.some((r) => r && METRIC_KEYS.some((k) => r[k] != null));

const startedAtMs = (entry) => {
  const t = new Date(entry?.startedAt).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * Wall-clock epoch ms of the entry's last activity, with a fallback chain for
 * legacy entries written before `updatedAt` shipped:
 *   updatedAt → startedAt + lastSampleT*1000 → startedAt
 */
export const entryLastActivityMs = (entry) => {
  if (Number.isFinite(entry?.updatedAt)) return entry.updatedAt;
  const start = startedAtMs(entry);
  if (start == null) return null;
  const samples = entry?.samples;
  const lastT = Array.isArray(samples)
    ? samples[samples.length - 1]?.t
    : undefined;
  return Number.isFinite(lastT) ? start + lastT * 1000 : start;
};

/**
 * Choose which in-flight entry (if any) to surface, and which to prune.
 *
 * Pure: no IDB, no localStorage, no Date.now() — caller passes `now` and the
 * already-loaded `dismissedMap` ({ [sessionId]: dismissedAtMs }).
 *
 * @returns {{ entry: object|null, pruneSessionIds: string[] }}
 *   `entry` is the most-recent recoverable entry; `pruneSessionIds` lists
 *   sessionIds safe to delete (passed the time gate but hold no real data).
 */
export const selectRecoverableEntry = (
  entries,
  now,
  dismissedMap = {},
  opts = {},
) => {
  const idleThresholdMs = opts.idleThresholdMs ?? DEFAULT_IDLE_THRESHOLD_MS;
  const maxAgeMs = opts.maxAgeMs ?? MAX_AGE_MS;
  const list = Array.isArray(entries) ? entries : [];

  const candidates = [];
  const pruneSessionIds = [];

  for (const entry of list) {
    const start = startedAtMs(entry);
    if (start == null) continue;

    // Skip entries the user closed — unless the dismissal itself has expired,
    // so "Close" never suppresses an entry forever.
    const dismissedAt = dismissedMap[entry.sessionId];
    if (Number.isFinite(dismissedAt) && now - dismissedAt <= maxAgeMs) continue;

    const age = now - start;
    if (age > maxAgeMs) continue; // genuinely stale — abandon quietly

    const idle = now - (entryLastActivityMs(entry) ?? start);
    if (idle < idleThresholdMs) continue; // still recent — don't nag

    if (hasMeaningfulData(entry)) candidates.push(entry);
    else if (entry.sessionId) pruneSessionIds.push(entry.sessionId);
  }

  candidates.sort(
    (a, b) => entryLastActivityMs(b) - entryLastActivityMs(a),
  );

  return { entry: candidates[0] || null, pruneSessionIds };
};

// ── Dismissal store (localStorage) ──────────────────────────────────────────

/** Load the dismissed-session map, pruning entries older than MAX_AGE_MS. */
export const loadDismissed = () => {
  const raw = localStorageService.get(DISMISSED_KEY);
  if (!raw || typeof raw !== "object") return {};
  const now = Date.now();
  const pruned = {};
  for (const [sessionId, dismissedAt] of Object.entries(raw)) {
    if (Number.isFinite(dismissedAt) && now - dismissedAt <= MAX_AGE_MS) {
      pruned[sessionId] = dismissedAt;
    }
  }
  return pruned;
};

/** Remember a non-destructive "Close" so the entry stops re-nagging. */
export const recordDismissed = (sessionId) => {
  if (!sessionId) return;
  const next = loadDismissed();
  next[sessionId] = Date.now();
  localStorageService.set(DISMISSED_KEY, next);
};

// ── Already saved? ──────────────────────────────────────────────────────────

/** Wall-clock [start, end] ms the entry's recording covers, or null. */
export const entryWindowMs = (entry) => {
  const start = startedAtMs(entry);
  if (start == null) return null;
  const lastT = entry?.samples?.[entry.samples.length - 1]?.t;
  return [start, start + (Number.isFinite(lastT) ? lastT : 0) * 1000];
};

/** Seconds of heart-rate or machine trace a saved session already holds. */
export const savedTraceSeconds = (session) =>
  Number(session?.telemetry_summary?.duration_s) || 0;

/**
 * The saved session this recording belongs to: the one whose time overlaps
 * it most, or null. A recording left behind after the session was saved (a
 * live session the server filed, or one split in two by a recorder restart)
 * belongs to that session, not in a new one beside it.
 */
export const findOverlappingSession = (entry, sessions) => {
  const win = entryWindowMs(entry);
  if (!win || !Array.isArray(sessions)) return null;
  let best = null;
  let bestOverlap = 0;
  for (const s of sessions) {
    const start = new Date(s?.started_at).getTime();
    if (!Number.isFinite(start)) continue;
    let end = s?.ended_at ? new Date(s.ended_at).getTime() : NaN;
    if (!Number.isFinite(end)) end = start + (Number(s?.duration_minutes) || 0) * 60_000;
    const overlap = Math.min(end, win[1]) - Math.max(start, win[0]);
    if (overlap > bestOverlap) {
      best = s;
      bestOverlap = overlap;
    }
  }
  return best;
};
